import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './PushModal.css';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import unifiedGitDataService from '../services/UnifiedGitDataService';
import commitMessageStorage from '../utils/commitMessageStorage';

/**
 * Advanced Git Push Modal
 * Features:
 * - Shows local modified files
 * - Shows remote changes
 * - Conflict detection and resolution
 * - Pull and merge capabilities
 * - Commit message input
 * - Push progress tracking
 * - Result display
 */
const PushModal = ({ isOpen, onClose, projectId, serviceName, gitProjectId, repositoryUrl, branch }) => {
  const [step, setStep] = useState('loading'); // loading, review, conflicts, pushing, success, error
  const [localFiles, setLocalFiles] = useState([]);
  const [remoteFiles, setRemoteFiles] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [commitMessage, setCommitMessage] = useState('');
  const [pushProgress, setPushProgress] = useState({ current: 0, total: 0, message: '' });
  const [error, setError] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [conflictResolutions, setConflictResolutions] = useState({}); // path -> 'local' | 'remote' | 'merged'
  
  // Refs for synchronized scrolling
  const localPreRef = useRef(null);
  const remotePreRef = useRef(null);
  const isSyncingScrollRef = useRef(false);

  /**
   * Compute line-by-line diff between local and remote content
   * Returns arrays of line objects with type: 'unchanged', 'modified', 'added', 'removed'
   */
  const computeDiff = useMemo(() => {
    if (!selectedConflict) return { localLines: [], remoteLines: [] };

    const localLines = selectedConflict.localContent.split('\n');
    const remoteLines = selectedConflict.remoteContent.split('\n');
    const localResult = [];
    const remoteResult = [];

    // Simple line-by-line comparison
    const maxLines = Math.max(localLines.length, remoteLines.length);
    
    for (let i = 0; i < maxLines; i++) {
      const localLine = i < localLines.length ? localLines[i] : null;
      const remoteLine = i < remoteLines.length ? remoteLines[i] : null;

      if (localLine === null) {
        // Line only exists in remote (added)
        remoteResult.push({ line: remoteLine, type: 'added', lineNum: i + 1 });
        localResult.push({ line: '', type: 'empty', lineNum: i + 1 });
      } else if (remoteLine === null) {
        // Line only exists in local (removed from remote perspective)
        localResult.push({ line: localLine, type: 'removed', lineNum: i + 1 });
        remoteResult.push({ line: '', type: 'empty', lineNum: i + 1 });
      } else if (localLine === remoteLine) {
        // Lines are identical
        localResult.push({ line: localLine, type: 'unchanged', lineNum: i + 1 });
        remoteResult.push({ line: remoteLine, type: 'unchanged', lineNum: i + 1 });
      } else {
        // Lines are different
        localResult.push({ line: localLine, type: 'modified', lineNum: i + 1 });
        remoteResult.push({ line: remoteLine, type: 'modified', lineNum: i + 1 });
      }
    }

    return { localLines: localResult, remoteLines: remoteResult };
  }, [selectedConflict]);

  /**
   * Categorize error and provide helpful context
   */
  const categorizeError = (err) => {
    const errorMessage = err.message || String(err);
    const errorStack = err.stack;

    // Check for specific error types
    if (errorMessage.includes('GitLab project not configured') || errorMessage.includes('repository integration')) {
      return {
        type: 'configuration',
        icon: '⚙️',
        title: 'Configuration Error',
        message: 'GitLab repository is not configured for this service.',
        suggestions: [
          'Go to project settings',
          'Navigate to the Git Integration section',
          'Configure your GitLab project ID and repository URL',
          'Make sure the branch name is correct'
        ],
        technicalDetails: errorMessage
      };
    }

    if (errorMessage.includes('No files to push') || errorMessage.includes('Make some changes')) {
      return {
        type: 'validation',
        icon: '📝',
        title: 'No Changes Detected',
        message: 'There are no modified or deleted files to push.',
        suggestions: [
          'Make changes to at least one file',
          'Save your changes before pushing',
          'Check if your changes were already pushed'
        ],
        technicalDetails: errorMessage
      };
    }

    // Check auth errors BEFORE generic network errors — a 401 response can produce a message
    // that contains 'Failed to fetch' (e.g. 'Failed to fetch repository tree: Unauthorized'),
    // which would otherwise be misclassified as a network connectivity problem.
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('authentication') || errorMessage.includes('Authentication required') || errorMessage.includes('reconnect')) {
      return {
        type: 'auth',
        icon: '🔐',
        title: 'Authentication Error',
        message: 'Authentication with GitLab failed.',
        suggestions: [
          'Check your GitLab access token',
          'Verify that your token has the required permissions (api, write_repository)',
          'Re-authenticate with GitLab if needed',
          'Contact your administrator if you don\'t have access'
        ],
        technicalDetails: errorMessage
      };
    }

    if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION')) {
      return {
        type: 'network',
        icon: '🌐',
        title: 'Network Error',
        message: 'Unable to connect to GitLab server.',
        suggestions: [
          'Check your internet connection',
          'Verify that GitLab is accessible',
          'Check if you need to configure a proxy',
          'Try again in a few moments'
        ],
        technicalDetails: errorMessage
      };
    }

    if (errorMessage.includes('403') || errorMessage.includes('Forbidden') || errorMessage.includes('permission')) {
      return {
        type: 'permission',
        icon: '🚫',
        title: 'Permission Denied',
        message: 'You do not have permission to push to this repository.',
        suggestions: [
          'Verify that you have write access to the repository',
          'Check if the branch is protected',
          'Contact the repository owner for access',
          'Make sure your access token has write_repository permission'
        ],
        technicalDetails: errorMessage
      };
    }

    if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
      return {
        type: 'notfound',
        icon: '🔍',
        title: 'Repository Not Found',
        message: 'The GitLab repository or branch could not be found.',
        suggestions: [
          'Verify the GitLab project ID is correct',
          'Check that the repository URL is valid',
          'Ensure the branch name exists in the repository',
          'Confirm you have access to view the repository'
        ],
        technicalDetails: errorMessage
      };
    }

    if (errorMessage.includes('conflict') || errorMessage.includes('merge')) {
      return {
        type: 'conflict',
        icon: '⚠️',
        title: 'Merge Conflict',
        message: 'There are unresolved conflicts with the remote repository.',
        suggestions: [
          'Go back and resolve all conflicts',
          'Choose whether to keep local or remote version for each file',
          'Pull the latest changes from remote',
          'Try pushing again after resolving conflicts'
        ],
        technicalDetails: errorMessage
      };
    }

    // Generic error
    return {
      type: 'unknown',
      icon: '❌',
      title: 'Push Failed',
      message: errorMessage || 'An unexpected error occurred while pushing to Git.',
      suggestions: [
        'Check the error details below',
        'Verify your Git configuration',
        'Try again in a few moments',
        'Contact support if the problem persists'
      ],
      technicalDetails: errorStack || errorMessage
    };
  };

  useEffect(() => {
    if (isOpen && projectId && serviceName) {
      loadChanges();
    }
  }, [isOpen, projectId, serviceName]);

  // Setup synchronized scrolling for diff viewer
  useEffect(() => {
    const localPre = localPreRef.current;
    const remotePre = remotePreRef.current;

    if (!localPre || !remotePre) return;

    const handleLocalScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      
      // Sync scroll position and percentage
      const scrollPercentage = localPre.scrollTop / (localPre.scrollHeight - localPre.clientHeight);
      remotePre.scrollTop = scrollPercentage * (remotePre.scrollHeight - remotePre.clientHeight);
      
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };

    const handleRemoteScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      
      // Sync scroll position and percentage
      const scrollPercentage = remotePre.scrollTop / (remotePre.scrollHeight - remotePre.clientHeight);
      localPre.scrollTop = scrollPercentage * (localPre.scrollHeight - localPre.clientHeight);
      
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };

    localPre.addEventListener('scroll', handleLocalScroll);
    remotePre.addEventListener('scroll', handleRemoteScroll);

    return () => {
      localPre.removeEventListener('scroll', handleLocalScroll);
      remotePre.removeEventListener('scroll', handleRemoteScroll);
    };
  }, [selectedConflict]);

  const isAuthError = (err) => {
    const msg = err?.message || String(err);
    return msg.includes('401') || msg.includes('Unauthorized') || msg.includes('authentication') ||
      msg.includes('Authentication required') || msg.includes('reconnect');
  };

  /**
   * Refresh the authtool-bff session (mirrors a page refresh).
   * The Vite proxy routes GET /bff/session → authtool-bff, which re-validates
   * the login token and creates a new internal session.
   * This must be called before retrying after a 401 — a blind wait won't help
   * because the session won't self-heal without this call.
   */
  const refreshSession = async () => {
    try {
      const res = await fetch('/bff/session', { method: 'GET', credentials: 'include' });
      console.log(`🔄 PushModal: Session refresh HTTP ${res.status}`);
      return res.ok;
    } catch (e) {
      console.warn('⚠️ PushModal: Session refresh failed:', e.message);
      return false;
    }
  };

  const loadChanges = async (retryCount = 0) => {
    const MAX_AUTH_RETRIES = 3;

    try {
      setStep('loading');
      setError(null);

      console.log('🔄 PushModal: Loading changes...', { projectId, serviceName, gitProjectId, branch, retryCount });

      // Load local changes first (this works without GitLab)
      const modifiedFilesData = await indexedDbFileStorage.getModifiedFiles(projectId, serviceName);
      const deletedFilesData = await indexedDbFileStorage.getDeletedFiles(projectId, serviceName);
      
      console.log('📦 PushModal: Local changes found:', {
        modified: modifiedFilesData.length,
        deleted: deletedFilesData.length,
        files: modifiedFilesData.map(f => ({ path: f.filePath, state: f.state }))
      });

      const local = [
        ...modifiedFilesData.map(f => {
          const gitState = f.gitState || f.state || 'modified';
          return {
            path: f.filePath,
            type: gitState === 'new' ? 'new' : 'modified',
            gitState,
            content: f.content,
            originalContent: f.originalContent
          };
        }),
        ...deletedFilesData.map(f => ({
          path: f.filePath,
          type: 'deleted',
          gitState: 'deleted'
        }))
      ];
      setLocalFiles(local);

      // If no local changes, show error
      if (local.length === 0) {
        const errorInfo = categorizeError(new Error('No files to push. Make some changes first.'));
        setError(errorInfo);
        setStep('error');
        return;
      }

      // Validate GitLab configuration
      if (!gitProjectId) {
        console.warn('⚠️ PushModal: No GitLab project configured, skipping conflict detection');
        // Show files for review without conflict detection
        setCommitMessage(`Update ${local.length} file(s)`);
        setStep('review');
        return;
      }

      // Load remote changes for conflict detection
      console.log('🌐 PushModal: Fetching remote repository tree...');
      
      const gitlabTree = await unifiedGitDataService.getRepositoryTree(gitProjectId, repositoryUrl, '', branch);
      const existingFiles = new Map(gitlabTree.filter(item => item.type === 'blob').map(item => [item.path, item]));

      // Check for conflicts
      const remoteChanges = [];
      const detectedConflicts = [];

      for (const localFile of modifiedFilesData) {
        if (existingFiles.has(localFile.filePath)) {
          try {
            const remoteFile = await unifiedGitDataService.getFileContent(gitProjectId, repositoryUrl, localFile.filePath, branch);

            // Check if remote differs from our original
            if (localFile.originalContent && remoteFile.decodedContent !== localFile.originalContent) {
              remoteChanges.push({
                path: localFile.filePath,
                content: remoteFile.decodedContent
              });

              // Check if local also differs from original (conflict)
              if (localFile.content !== localFile.originalContent) {
                detectedConflicts.push({
                  path: localFile.filePath,
                  localContent: localFile.content,
                  remoteContent: remoteFile.decodedContent,
                  originalContent: localFile.originalContent
                });
              }
            }
          } catch (err) {
            console.warn(`Could not fetch ${localFile.filePath}:`, err);
          }
        }
      }

      setRemoteFiles(remoteChanges);
      setConflicts(detectedConflicts);

      // Load accumulated commit message
      try {
        const tenantId = localStorage.getItem('selectedTenantId');
        const accumulatedMessage = await commitMessageStorage.getMessage(tenantId, projectId, serviceName);
        if (accumulatedMessage) {
          setCommitMessage(accumulatedMessage.substring(0, 2000)); // Limit to 2KB
        } else {
          setCommitMessage(`Update ${local.length} file(s)`);
        }
      } catch (err) {
        setCommitMessage(`Update ${local.length} file(s)`);
      }

      setStep(detectedConflicts.length > 0 ? 'conflicts' : 'review');
    } catch (err) {
      // If this is a transient auth error (authtool-bff session expired mid-session),
      // retry silently before showing the error to the user — same pattern as checkLinkStatus.
      if (isAuthError(err) && retryCount < MAX_AUTH_RETRIES) {
        console.warn(
          `⚠️ PushModal: Auth error loading changes, refreshing session then retrying ` +
          `(attempt ${retryCount + 1}/${MAX_AUTH_RETRIES})…`, err.message
        );
        // Refresh the authtool-bff session before retrying — this is what page refresh
        // does implicitly; a blind wait+retry would hit the same dead session.
        await refreshSession();
        return loadChanges(retryCount + 1);
      }
      console.error('Failed to load changes:', err);
      const errorInfo = categorizeError(err);
      setError(errorInfo);
      setStep('error');
    }
  };

  const handlePullAndMerge = async () => {
    try {
      setPushProgress({ current: 0, total: remoteFiles.length, message: 'Pulling remote changes...' });
      
      for (let i = 0; i < remoteFiles.length; i++) {
        const remoteFile = remoteFiles[i];
        const localFile = localFiles.find(f => f.path === remoteFile.path);
        
        // If no conflict, just update local with remote
        if (!conflicts.find(c => c.path === remoteFile.path)) {
          await indexedDbFileStorage.saveFile(
            projectId,
            serviceName,
            remoteFile.path,
            remoteFile.content,
            { originalContent: remoteFile.content }
          );
        }
        
        setPushProgress({ current: i + 1, total: remoteFiles.length, message: `Merged ${remoteFile.path}` });
      }

      // Reload changes after merge
      await loadChanges();
    } catch (err) {
      console.error('Failed to pull and merge:', err);
      const errorInfo = categorizeError(err);
      setError(errorInfo);
      setStep('error');
    }
  };

  const resolveConflict = (path, resolution) => {
    setConflictResolutions({
      ...conflictResolutions,
      [path]: resolution
    });
  };

  const handlePush = async () => {
    try {
      setStep('pushing');
      setPushProgress({ current: 0, total: 100, message: 'Starting push...' });

      // Validate GitLab configuration
      if (!gitProjectId) {
        throw new Error('GitLab project not configured. Please set up repository integration in project settings.');
      }

      // Apply conflict resolutions
      for (const conflict of conflicts) {
        const resolution = conflictResolutions[conflict.path] || 'local';
        if (resolution === 'remote') {
          await indexedDbFileStorage.saveFile(
            projectId,
            serviceName,
            conflict.path,
            conflict.remoteContent,
            { originalContent: conflict.originalContent }
          );
        }
      }

      setPushProgress({ current: 20, total: 100, message: 'Preparing files...' });

      // Get final list of changes
      const modifiedFilesData = await indexedDbFileStorage.getModifiedFiles(projectId, serviceName);
      const deletedFilesData = await indexedDbFileStorage.getDeletedFiles(projectId, serviceName);

      console.log('🌐 Fetching repository tree to determine file actions...');
      const gitlabTree = await unifiedGitDataService.getRepositoryTree(gitProjectId, repositoryUrl, '', branch);
      const existingFiles = new Set(gitlabTree.filter(item => item.type === 'blob').map(item => item.path));

      setPushProgress({ current: 40, total: 100, message: 'Building commit actions...' });

      const actions = [];
      
      // File actions
      for (const file of modifiedFilesData) {
        const action = existingFiles.has(file.filePath) ? 'update' : 'create';
        actions.push({
          action,
          file_path: file.filePath,
          content: file.content,
          encoding: file.isBinary ? 'base64' : undefined
        });
      }

      // Delete actions
      for (const file of deletedFilesData) {
        if (existingFiles.has(file.filePath)) {
          actions.push({
            action: 'delete',
            file_path: file.filePath
          });
        }
      }

      setPushProgress({ current: 60, total: 100, message: 'Committing to GitLab...' });

      // Truncate commit message if needed
      let finalMessage = commitMessage;
      if (finalMessage.length > 2000) {
        finalMessage = finalMessage.substring(0, 1950) + '\n\n... (truncated)';
      }

      // Push to GitLab
      await unifiedGitDataService.commitChanges(
        gitProjectId,
        repositoryUrl,
        branch,
        finalMessage,
        actions
      );

      setPushProgress({ current: 80, total: 100, message: 'Updating local state...' });

      // Mark files as sent
      for (const file of modifiedFilesData) {
        await indexedDbFileStorage.markAsSent(projectId, serviceName, file.filePath);
      }
      for (const file of deletedFilesData) {
        await indexedDbFileStorage.deleteFile(projectId, serviceName, file.filePath);
      }

      // Clear commit messages
      try {
        const tenantId = localStorage.getItem('selectedTenantId');
        await commitMessageStorage.clearMessage(tenantId, projectId, serviceName);
      } catch (err) {
        console.warn('Failed to clear commit messages:', err);
      }

      setPushProgress({ current: 100, total: 100, message: 'Push completed!' });
      setStep('success');

      // Dispatch success event
      window.dispatchEvent(new CustomEvent('gitPushCompleted', {
        detail: {
          success: true,
          projectId,
          serviceName,
          branch,
          filesCount: actions.length
        }
      }));

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Failed to push:', err);
      const errorInfo = categorizeError(err);
      setError(errorInfo);
      setStep('error');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="push-modal-overlay" onClick={onClose}>
      <div className="push-modal" onClick={(e) => e.stopPropagation()}>
        <div className="push-modal-header">
          <h2>🚀 Push to Git</h2>
          <button className="push-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="push-modal-body">
          {step === 'loading' && (
            <div className="push-modal-loading">
              <div className="spinner"></div>
              <p>Loading changes...</p>
            </div>
          )}

          {step === 'review' && (
            <>
              <div className="push-modal-section">
                <h3>📝 Local Changes ({localFiles.length})</h3>
                <div className="file-list">
                  {localFiles.map(file => (
                    <div key={file.path} className={`file-item file-${file.type}`}>
                      <span className="file-path">{file.path}</span>
                      <span className="file-badge">
                        {file.type === 'deleted' ? 'Deleted' : file.type === 'new' ? 'New' : 'Modified'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {remoteFiles.length > 0 && (
                <div className="push-modal-section">
                  <h3>🌐 Remote Changes ({remoteFiles.length})</h3>
                  <div className="file-list">
                    {remoteFiles.map(file => (
                      <div key={file.path} className="file-item file-remote">
                        <span className="file-icon">☁️</span>
                        <span className="file-path">{file.path}</span>
                        <span className="file-badge">remote</span>
                      </div>
                    ))}
                  </div>
                  <button className="btn-secondary" onClick={handlePullAndMerge}>
                    🔄 Pull & Merge Remote Changes
                  </button>
                </div>
              )}

              <div className="push-modal-section">
                <h3>💬 Commit Message</h3>
                <textarea
                  className="commit-message-input"
                  value={commitMessage}
                  onChange={(e) => setCommitMessage(e.target.value)}
                  placeholder="Enter commit message..."
                  rows={5}
                />
                <div className="commit-message-info">
                  {commitMessage.length} / 2000 characters
                </div>
              </div>

              {!gitProjectId && (
                <div className="push-modal-warning">
                  ⚠️ GitLab repository not configured for this service. Configure it in project settings to enable push.
                </div>
              )}

              <div className="push-modal-actions">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button 
                  className="btn-primary" 
                  onClick={handlePush} 
                  disabled={localFiles.length === 0 || !gitProjectId}
                  title={!gitProjectId ? 'GitLab repository not configured' : ''}
                >
                  🚀 Commit & Push ({localFiles.length} files)
                </button>
              </div>
            </>
          )}

          {step === 'conflicts' && (
            <>
              <div className="push-modal-section">
                <h3>⚠️ Conflicts Detected ({conflicts.length})</h3>
                <p className="conflict-warning">
                  The following files have been modified both locally and remotely.
                  Please choose how to resolve each conflict:
                </p>
                <div className="conflict-list">
                  {conflicts.map(conflict => (
                    <div key={conflict.path} className="conflict-item">
                      <div className="conflict-header">
                        <span className="conflict-path">📄 {conflict.path}</span>
                        <div className="conflict-actions">
                          <button
                            className={`conflict-btn ${conflictResolutions[conflict.path] === 'local' ? 'active' : ''}`}
                            onClick={() => resolveConflict(conflict.path, 'local')}
                          >
                            Use Local
                          </button>
                          <button
                            className={`conflict-btn ${conflictResolutions[conflict.path] === 'remote' ? 'active' : ''}`}
                            onClick={() => resolveConflict(conflict.path, 'remote')}
                          >
                            Use Remote
                          </button>
                          <button
                            className="conflict-btn-view"
                            onClick={() => setSelectedConflict(conflict)}
                          >
                            View Diff
                          </button>
                        </div>
                      </div>
                      {conflictResolutions[conflict.path] && (
                        <div className="conflict-resolution">
                          ✓ Resolved: Will use <strong>{conflictResolutions[conflict.path]}</strong> version
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {selectedConflict && (
                <div className="conflict-diff-viewer">
                  <div className="diff-header">
                    <h4>{selectedConflict.path}</h4>
                    <button onClick={() => setSelectedConflict(null)}>✕</button>
                  </div>
                  <div className="diff-content">
                    <div className="diff-column">
                      <h5>Local Version</h5>
                      <pre ref={localPreRef}>
                        {computeDiff.localLines.map((lineObj, idx) => (
                          <div key={idx} className={`diff-line diff-line-${lineObj.type}`}>
                            <span className="diff-line-num">{lineObj.lineNum}</span>
                            <span className="diff-line-content">{lineObj.line || '\n'}</span>
                          </div>
                        ))}
                      </pre>
                    </div>
                    <div className="diff-column">
                      <h5>Remote Version</h5>
                      <pre ref={remotePreRef}>
                        {computeDiff.remoteLines.map((lineObj, idx) => (
                          <div key={idx} className={`diff-line diff-line-${lineObj.type}`}>
                            <span className="diff-line-num">{lineObj.lineNum}</span>
                            <span className="diff-line-content">{lineObj.line || '\n'}</span>
                          </div>
                        ))}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              <div className="push-modal-actions">
                <button className="btn-secondary" onClick={() => setStep('review')}>Back</button>
                <button 
                  className="btn-primary" 
                  onClick={handlePush}
                  disabled={conflicts.some(c => !conflictResolutions[c.path])}
                >
                  Continue to Push
                </button>
              </div>
            </>
          )}

          {step === 'pushing' && (
            <div className="push-modal-progress">
              <h3>📤 Pushing Changes...</h3>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(pushProgress.current / pushProgress.total) * 100}%` }}
                />
              </div>
              <p className="progress-message">{pushProgress.message}</p>
              <p className="progress-stats">
                {pushProgress.current} / {pushProgress.total}
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="push-modal-result push-success">
              <div className="result-icon">✅</div>
              <h3>Push Successful!</h3>
              <p>All changes have been pushed to GitLab.</p>
            </div>
          )}

          {step === 'error' && (
            <div className="push-modal-result push-error">
              <div className="result-icon">{error?.icon || '❌'}</div>
              <h3>{error?.title || 'Push Failed'}</h3>

              <div className="error-message">
                {error?.message || error}
              </div>

              {error?.suggestions && error.suggestions.length > 0 && (
                <div className="error-suggestions">
                  <h4>💡 What you can do:</h4>
                  <ul>
                    {error.suggestions.map((suggestion, idx) => (
                      <li key={idx}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}

              {error?.technicalDetails && (
                <div className="error-details-container">
                  <button
                    className="error-details-toggle"
                    onClick={() => setShowErrorDetails(!showErrorDetails)}
                  >
                    {showErrorDetails ? '▼' : '▶'} Technical Details
                  </button>
                  {showErrorDetails && (
                    <pre className="error-details-content">
                      {error.technicalDetails}
                    </pre>
                  )}
                </div>
              )}

              <div className="push-modal-actions">
                <button className="btn-secondary" onClick={onClose}>Close</button>
                <button className="btn-primary" onClick={loadChanges}>🔄 Retry</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PushModal;
